/* PrimeCut Singles buy box (snippets/pc-buybox): size -> quantity -> preset colors or a custom pack -> add to cart,
   without leaving the page.

   What reaches the cart is always something the store already sells at that price:
   - Single        the chosen tee variant.
   - Preset pack   the preset-pack product's variant for the chosen size (a Shopify component bundle), with the gift lines
                   of the Kaching Bundles deal on that pack, exactly as the collection quick-add sends them.
   - Custom pack   the chosen tee variants, sent the way Kaching Bundles' own widget sends its quantity-break deal for
                   that tee count, so Kaching's discount prices the pack at checkout. The deal is read from the page,
                   never hard-coded. A pack size without such a deal bar has no real custom price, so it cannot be
                   customized or added: the Build Your Own card says so instead.
   A pack opened with Customize but left with the preset's exact colors is still that preset, and is added as it.
   assets/pc-cart-gift.js keeps the gift rules true afterwards (one $10 card per bundle, one promotional e-book per cart). */
(() => {
  'use strict';

  const TEE_PATH = 'M22 6 8 14 2 27l10 5 4-5v31h32V27l4 5 10-5-6-13-14-8c-2 5-6 7-10 7s-8-2-10-7Z';
  const svgTee = (fill) =>
    `<svg class="pc-bb__tee" viewBox="0 0 64 64" aria-hidden="true" focusable="false"><path d="${TEE_PATH}" fill="${fill}" stroke="rgba(0,0,0,.4)" stroke-width="1.4" stroke-linejoin="round"${fill === 'none' ? ' stroke-dasharray="4 3"' : ''}/></svg>`;
  const CHECK = '<svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"><path d="m3 8.5 3.2 3.2L13 4.8" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const NUMBER_WORDS = { 2: 'two', 3: 'three', 4: 'four', 5: 'five', 6: 'six', 7: 'seven', 8: 'eight', 9: 'nine', 10: 'ten', 12: 'twelve' };

  const el = (tag, attrs, children) => {
    const node = document.createElement(tag);
    Object.entries(attrs || {}).forEach(([key, value]) => {
      if (value === false || value === null || value === undefined) return;
      if (key === 'class') node.className = value;
      else if (key === 'html') node.innerHTML = value;
      else if (key === 'text') node.textContent = value;
      else node.setAttribute(key, value === true ? '' : value);
    });
    (children || []).forEach((child) => child && node.appendChild(child));
    return node;
  };
  const readJson = (node) => {
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  };
  const numericId = (gid) => Number(String(gid).split('/').pop());
  const groupId = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const bytes = new Uint8Array(4);
    window.crypto.getRandomValues(bytes);
    return [...bytes].map((b) => chars[b % chars.length]).join('');
  };
  const kachingData = (item) => {
    const raw = item && item.properties && item.properties.__kaching_bundles;
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  };
  const calm = () => (window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');

  function init(root) {
    if (root.dataset.bbReady) return;
    const data = readJson(root.querySelector('[data-bb-data]'));
    if (!data) return;
    root.dataset.bbReady = 'true';
    const cfg = data.settings;
    const section = root.closest('[data-pc-pdp]') || document;
    const $ = (selector) => root.querySelector(selector);
    const ui = {
      sizeValue: $('[data-bb-size-value]'),
      sizeError: $('[data-bb-size-error]'),
      sizeGroup: $('[data-bb-size-group]'),
      qtyValue: $('[data-bb-qty-value]'),
      tiers: $('[data-bb-tiers]'),
      was: $('[data-bb-was]'),
      colorValue: $('[data-bb-color-value]'),
      cards: $('[data-bb-cards]'),
      notice: $('[data-bb-notice]'),
      colorError: $('[data-bb-color-error]'),
      sumPack: $('[data-bb-sum-pack]'),
      sumColors: $('[data-bb-sum-colors]'),
      now: $('[data-bb-now]'),
      gifts: $('[data-bb-gifts]'),
      giftLine: $('[data-bb-gift-line]'),
      giftShip: $('[data-bb-gift-ship]'),
      arriveRange: $('[data-bb-arrive-range]'),
      arriveNote: $('[data-bb-arrive-note]'),
      ship: $('[data-bb-ship]'),
      addError: $('[data-bb-add-error]'),
      atc: $('[data-bb-atc]'),
      atcLabel: $('[data-bb-atc-label]'),
      status: $('[data-bb-status]'),
      dialog: $('[data-bb-dialog]'),
      dName: $('[data-bb-d-name]'),
      dSub: $('[data-bb-d-sub]'),
      dCount: $('[data-bb-d-count]'),
      dBar: $('[data-bb-d-bar]'),
      dSlots: $('[data-bb-d-slots]'),
      dPick: $('[data-bb-d-pick]'),
      dColors: $('[data-bb-d-colors]'),
      dClear: $('[data-bb-d-clear]'),
      dReset: $('[data-bb-d-reset]'),
      dDone: $('[data-bb-d-done]'),
    };
    const sticky = section.querySelector('[data-pc-sticky]');
    const stickyBtn = sticky && sticky.querySelector('[data-pc-sticky-btn]');
    const stickySum = sticky && sticky.querySelector('[data-bb-sticky-sum]');
    const stickyPrice = sticky && sticky.querySelector('[data-pc-sticky-price]');

    /* ---------- store data ---------- */
    const colorByName = new Map(data.colors.map((color) => [color.name, color]));
    const teeVariant = (color, size) => data.variants.find((v) => v.color === color && v.size === size) || null;
    const anyVariant = (color) => data.variants.find((v) => v.color === color) || data.variants[0];
    const teePrice = (color, size) => {
      const variant = (size && teeVariant(color, size)) || anyVariant(color);
      return variant ? variant.price : 0;
    };
    // Only packs whose every color is a real tee color are offered.
    const packs = data.packs.filter((pack) => pack.contents.every((part) => colorByName.has(part.color)));
    const packVariant = (pack, size) => pack.variants.find((v) => v.size === size) || null;
    const expand = (pack) => pack.contents.flatMap((part) => Array.from({ length: part.quantity }, () => part.color));
    // Display name only ("The Workweek Five — 5-Pack" -> "Workweek Five"); the Shopify product keeps its title.
    const shortName = (pack) => pack.title.split(/\s+[—–-]\s+/)[0].replace(/^The\s+/i, '');
    // The order PrimeCut's own pack summaries list colors in, so a pack reads the same way everywhere.
    const colorOrder = [...packs]
      .sort((a, b) => b.contents.length - a.contents.length)
      .flatMap((pack) => pack.contents.map((part) => part.color))
      .concat(data.colors.map((color) => color.name))
      .filter((name, i, all) => all.indexOf(name) === i);
    const countColors = (colors) => {
      const counts = new Map();
      colors.filter(Boolean).forEach((color) => counts.set(color, (counts.get(color) || 0) + 1));
      return colorOrder.filter((name) => counts.has(name)).map((name) => ({ color: name, quantity: counts.get(name) }));
    };
    const summarize = (colors) =>
      countColors(colors)
        .map((part) => `${part.quantity} ${part.color}`)
        .join(' + ');

    // Kaching Bundles' deal for this tee. snippets/pc-buybox keeps a copy before removing the node, so Kaching's own
    // widget never starts on this page.
    const kachingPage = window.PrimeCutBuyboxKaching || {
      deals: [...document.querySelectorAll('script.kaching-bundles-deal-block-settings')].map(readJson).filter(Boolean),
      products: [...document.querySelectorAll('script.kaching-bundles-product')].map(readJson).filter(Boolean),
    };
    const packBars = (d) => (d.dealBars || []).filter((bar) => bar.dealBarType === 'quantity-break' && bar.quantity > 1 && bar.discountType === 'specific' && bar.discountValue > 0);
    // More than one Kaching deal can cover this tee. The one pricing the most pack sizes is used for every custom
    // pack, so a cart never mixes deals; with equal coverage the first one printed wins.
    const deal =
      kachingPage.deals
        .filter((d) => d && d.nanoId && Array.isArray(d.selectedProductIds) && d.selectedProductIds.includes(data.productId))
        .sort((a, b) => packBars(b).length - packBars(a).length)[0] || null;
    const kachingProducts = kachingPage.products;
    const customBar = (qty) =>
      (deal &&
        (deal.dealBars || []).find(
          (bar) => bar.dealBarType === 'quantity-break' && bar.quantity === qty && bar.discountType === 'specific' && bar.discountValue > 0
        )) ||
      null;
    const currencyRate = () => {
      const currency = window.Shopify && window.Shopify.currency;
      if (!currency || currency.active === cfg.shopCurrency) return 1;
      return parseFloat(currency.rate) || 1;
    };
    const customPrice = (qty) => {
      const bar = customBar(qty);
      return bar ? Math.round(bar.discountValue * 100 * currencyRate()) : null;
    };

    const tiers = String(cfg.tiers)
      .split(',')
      .map((t) => parseInt(t, 10))
      .filter((t) => t === 1 || (t > 1 && (packs.some((p) => p.qty === t) || customBar(t))));
    if (!tiers.length) tiers.push(1);
    const customTiers = tiers.filter((t) => t > 1 && customBar(t));
    const maxTier = Math.max(...tiers);
    const tierName = (qty) => (qty === 1 ? 'Single' : `${qty}-Pack`);
    const tierPacks = (qty) => packs.filter((p) => p.qty === qty);

    const money = (cents) => {
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || cfg.shopCurrency || 'USD';
      try {
        return new Intl.NumberFormat(cfg.locale || 'en', { style: 'currency', currency }).format(cents / 100);
      } catch (e) {
        return (cents / 100).toFixed(2);
      }
    };
    const moneyShort = (cents) => (cents % 100 === 0 ? money(cents).replace(/[.,]00(?=\D*$)/, '') : money(cents));

    /* ---------- state ---------- */
    // The quantity selected on arrival is the one set in the theme editor. If that size is not offered here (for
    // example it was removed from the list), fall back to the smallest pack, never simply to whatever is listed first.
    const packTiers = tiers.filter((t) => t > 1).sort((x, y) => x - y);
    const defaultQty = tiers.includes(Number(cfg.defaultQty)) ? Number(cfg.defaultQty) : packTiers.length ? packTiers[0] : tiers[0];
    // An explicit configuration link may carry ?qty=N (the homepage button uses ?qty=3). It sets the quantity shown on
    // arrival and wins over a remembered choice; an unknown or unoffered value is ignored.
    const linkedQty = (() => {
      try {
        const raw = new URLSearchParams(window.location.search).get('qty');
        const value = raw === null ? NaN : Number(raw);
        return tiers.includes(value) ? value : null;
      } catch (e) {
        return null;
      }
    })();
    // The shopper's own choices (quantity, size, colors) are remembered for this browser session only, so they are
    // still there after a reload, the back button or a trip to the cart. Nothing is remembered until they change
    // something, so a first visit always opens on the configured default (the 3-pack and its best-selling preset).
    const MEMORY_KEY = `pc-bb-choice-${data.productId}`;
    const remembered = (() => {
      try {
        const saved = JSON.parse(sessionStorage.getItem(MEMORY_KEY) || 'null');
        return saved && typeof saved === 'object' ? saved : null;
      } catch (e) {
        return null;
      }
    })();
    const startQty = linkedQty !== null ? linkedQty : remembered && tiers.includes(Number(remembered.qty)) ? Number(remembered.qty) : defaultQty;
    const state = {
      size: null,
      qty: startQty,
      single: null,
      choice: {}, // per pack size: { mode: 'preset', handle } | { mode: 'custom' }
      slots: Array.from({ length: maxTier }, () => null), // the editable pack; a smaller pack uses the first N
      dirty: false, // the slots hold the customer's own build, not just a preset opened for editing
      notice: '',
      busy: false,
      cartSubtotal: 0,
      active: 0, // slot being edited in the dialog
      customOpen: false, // the optional "Customize colors" disclosure
    };
    const defaultPreset = (qty) => {
      const list = tierPacks(qty);
      return list.find((p) => p.featured) || list.find((p) => p.best) || list[0] || null;
    };
    // Every fresh visit or reload starts on the arrival quantity and its featured preset with no size chosen. Nothing
    // from the URL (such as a ?variant= link) or an earlier visit is restored as the starting selection.
    if (state.qty > 1 && defaultPreset(state.qty)) state.choice[state.qty] = { mode: 'preset', handle: defaultPreset(state.qty).handle };
    // Restore the remembered choice, checking every part against what is offered now. The size is restored only when
    // it is the shopper's own earlier pick and still exists; it is never guessed.
    if (remembered) {
      if (typeof remembered.size === 'string' && data.sizes.includes(remembered.size) && data.variants.some((v) => v.size === remembered.size && v.available)) state.size = remembered.size;
      if (Number(remembered.qty) === state.qty && linkedQty === null) {
        if (state.qty === 1) {
          if (colorByName.has(remembered.single)) state.single = remembered.single;
        } else if (remembered.mode === 'preset' && packs.some((p) => p.handle === remembered.handle && p.qty === state.qty)) {
          state.choice[state.qty] = { mode: 'preset', handle: remembered.handle };
        } else if (remembered.mode === 'custom' && Array.isArray(remembered.slots) && customBar(state.qty)) {
          const slots = remembered.slots.slice(0, state.qty).map((color) => (colorByName.has(color) ? color : null));
          slots.forEach((color, i) => {
            state.slots[i] = color;
          });
          state.dirty = !!remembered.dirty;
          state.choice[state.qty] = { mode: 'custom' };
        }
      }
    }
    // A restored size is shown as selected in the size buttons too.
    if (state.size) {
      root.querySelectorAll('[data-bb-size]').forEach((input) => {
        input.checked = input.value === state.size;
      });
    }
    let touched = !!remembered;
    const remember = () => {
      if (!touched) return;
      try {
        const current = state.choice[state.qty] || null;
        sessionStorage.setItem(
          MEMORY_KEY,
          JSON.stringify({
            qty: state.qty,
            size: state.size,
            single: state.single,
            mode: current ? current.mode : null,
            handle: current && current.mode === 'preset' ? current.handle : null,
            slots: current && current.mode === 'custom' ? state.slots.slice(0, state.qty) : null,
            dirty: state.dirty,
          })
        );
      } catch (e) {
        /* storage unavailable: the choice simply lasts for this page view */
      }
    };

    const choice = () => state.choice[state.qty] || null;
    const isCustom = () => state.qty > 1 && !!choice() && choice().mode === 'custom';
    const visibleSlots = () => state.slots.slice(0, state.qty);
    const filled = () => visibleSlots().filter(Boolean).length;
    const colorOk = (color) => !state.size || !!(teeVariant(color, state.size) && teeVariant(color, state.size).available);
    const sameColors = (a, b) => summarize(a) === summarize(b) && a.filter(Boolean).length === b.length;
    // The preset of this pack size with exactly these colors, if any.
    const matchPreset = (colors) => tierPacks(state.qty).find((pack) => sameColors(colors, expand(pack))) || null;
    const selectedPack = () => {
      const current = choice();
      if (state.qty < 2 || !current) return null;
      if (current.mode === 'preset') return packs.find((p) => p.handle === current.handle) || null;
      return filled() === state.qty ? matchPreset(visibleSlots()) : null;
    };

    /* ---------- what is selected, what it costs, what is missing ---------- */
    function selection() {
      const size = state.size;
      if (state.qty === 1) {
        const color = state.single;
        const variant = color && size ? teeVariant(color, size) : null;
        const price = teePrice(color || data.colors[0].name, size);
        return { kind: 'single', colors: color ? [color] : [], price, compare: price, variant, label: color || '' };
      }
      const pack = selectedPack();
      if (pack) {
        const variant = size ? packVariant(pack, size) : null;
        // Shown in the order the customer arranged them when they came through the editor.
        const colors = isCustom() ? visibleSlots() : expand(pack);
        return {
          kind: 'preset',
          pack,
          colors,
          variant,
          price: (variant || pack.variants[0]).price,
          compare: colors.reduce((sum, color) => sum + teePrice(color, size), 0),
          label: pack.summary || summarize(colors),
        };
      }
      if (isCustom()) {
        const colors = visibleSlots();
        const chosen = colors.filter(Boolean);
        return {
          kind: 'custom',
          colors,
          price: customPrice(state.qty),
          // An unfinished pack is compared against the same number of tees at the single price.
          compare: chosen.reduce((sum, color) => sum + teePrice(color, size), 0) + (state.qty - chosen.length) * teePrice(data.colors[0].name, size),
          label: chosen.length ? summarize(chosen) : '',
        };
      }
      const list = tierPacks(state.qty);
      const from = list.length ? Math.min(...list.map((p) => p.variants[0].price)) : customPrice(state.qty);
      return { kind: 'none', colors: [], price: from, compare: state.qty * teePrice(data.colors[0].name, size), label: '' };
    }

    // The first thing still missing, or { ready: true }.
    function readiness(sel) {
      if (!state.size) return { ready: false, step: 'size', label: cfg.chooseLabel, message: 'Choose a size before adding to cart.' };
      if (sel.kind === 'single') {
        if (!state.single) return { ready: false, step: 'colors', label: 'Choose a color', message: 'Choose a color before adding to cart.' };
        if (!sel.variant || !sel.variant.available) {
          return { ready: false, step: 'colors', label: 'Sold out', message: `${state.single} is sold out in ${state.size}. Choose another color or size.` };
        }
        return { ready: true };
      }
      if (sel.kind === 'preset') {
        if (!sel.variant || !sel.variant.available) {
          return { ready: false, step: 'colors', label: 'Sold out', message: `${shortName(sel.pack)} is sold out in ${state.size}. Choose another combination or size.` };
        }
        return { ready: true };
      }
      if (sel.kind === 'custom') {
        if (!customBar(state.qty) || sel.price === null) {
          return { ready: false, step: 'colors', label: 'Choose a color combination', message: `Custom color mixes can't be added for the ${tierName(state.qty)} yet. Choose a ready-made combination.` };
        }
        const missing = state.qty - filled();
        if (missing === state.qty) return { ready: false, step: 'pack', label: 'Choose your first color', message: `Choose ${state.qty} tees to build your pack.` };
        if (missing > 0) {
          return { ready: false, step: 'pack', label: `Choose ${missing} more ${missing === 1 ? 'tee' : 'tees'}`, message: `${filled()} of ${state.qty} tees selected. Choose ${missing} more to complete your pack.` };
        }
        const gone = sel.colors.find((color) => !colorOk(color));
        if (gone) return { ready: false, step: 'pack', label: 'Sold out', message: `${gone} is sold out in ${state.size}. Change that tee or choose another size.` };
        return { ready: true };
      }
      return { ready: false, step: 'colors', label: 'Choose a color combination', message: `Choose a color combination for your ${tierName(state.qty)}.` };
    }

    /* ---------- arrival estimate and shipping ---------- */
    // Estimated arrival in calendar days, counted from today's date in the store's timezone: processing (24-48 hours,
    // 1-2 days) plus the delivery window (7-15 days). Weekends and holidays count like any other day.
    const DAY = 86400000;
    const storeToday = () => {
      const now = new Date();
      try {
        const parts = new Intl.DateTimeFormat('en-US', { timeZone: cfg.timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
        const get = (type) => Number(parts.find((p) => p.type === type).value);
        return Date.UTC(get('year'), get('month') - 1, get('day'));
      } catch (e) {
        const m = /^([+-])(\d\d)(\d\d)$/.exec(cfg.tzOffset || '');
        const offset = m ? (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3])) : 0;
        const local = new Date(now.getTime() + offset * 60000);
        return Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate());
      }
    };
    // Delivery estimates after dispatch, in calendar days, for the four countries PrimeCut ships to (theme setting
    // "Delivery times by country"). The estimate shown is for the shopper's selected country when it is one of
    // these; otherwise it is the United States estimate and says so.
    const shipTimes = (cfg.shipTimes || []).filter((row) => row && row.code && row.min > 0 && row.max >= row.min);
    const destination = shipTimes.find((row) => row.code === String(cfg.countryCode || '').toUpperCase()) || shipTimes.find((row) => row.code === 'US') || null;
    function renderArrival() {
      const today = storeToday();
      const min = destination ? destination.min : Number(cfg.arriveMin) || 0;
      const max = destination ? destination.max : Number(cfg.arriveMax) || 0;
      const earliest = new Date(today + ((Number(cfg.processMin) || 0) + min) * DAY);
      const latest = new Date(today + ((Number(cfg.processMax) || 0) + max) * DAY);
      const format = (date) => {
        try {
          return new Intl.DateTimeFormat(cfg.locale || 'en', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(date);
        } catch (e) {
          return `${date.getUTCMonth() + 1}/${date.getUTCDate()}`;
        }
      };
      ui.arriveRange.textContent = `${format(earliest)} – ${format(latest)}`;
      const place = destination ? destination.name : cfg.country || 'your country';
      const where = root.querySelector('[data-bb-arrive-country]');
      if (where && destination) where.textContent = destination.name;
      const outside = root.querySelector('[data-bb-arrive-outside]');
      if (outside) outside.hidden = !(destination && destination.code === 'US');
      const note = String(cfg.arriveNote || '').replace('[country]', place).replace('[days]', `${min}–${max}`);
      ui.arriveNote.textContent = note;
      const more = root.querySelector('[data-bb-arrive-more]');
      if (more) more.hidden = !note;
    }
    // Free shipping: the order must reach the threshold (merchandise after discounts, in shop currency) and go to a
    // country with a free rate. 'free' = this purchase alone qualifies, 'cart' = it qualifies with the cart.
    const shipStatus = (sel) => {
      if (cfg.freeShipCountries.length && !cfg.freeShipCountries.includes(String(cfg.countryCode || '').toUpperCase())) return 'elsewhere';
      const threshold = Math.ceil(cfg.shipThreshold * currencyRate());
      const price = sel.price || 0;
      if (price >= threshold) return 'free';
      if (state.cartSubtotal + price >= threshold) return 'cart';
      return 'below';
    };
    function renderShipping(sel) {
      const rate = currencyRate();
      const threshold = Math.ceil(cfg.shipThreshold * rate);
      const status = shipStatus(sel);
      const check = CHECK.replace('width="12" height="12"', 'width="14" height="14"');
      ui.ship.classList.remove('is-free');
      if (status === 'free' || status === 'cart') {
        ui.ship.classList.add('is-free');
        ui.ship.innerHTML = `${check}<span></span>`;
        ui.ship.lastChild.textContent = status === 'free' ? cfg.shipFreeLabel : `${cfg.shipFreeLabel} with your cart`;
      } else if (status === 'elsewhere') {
        ui.ship.textContent = 'Shipping calculated at checkout';
      } else if (rate === 1 && cfg.shipRate) {
        ui.ship.textContent = `Shipping ${money(Math.round(parseFloat(cfg.shipRate) * 100))} · free on orders ${moneyShort(threshold)}+`;
      } else {
        ui.ship.textContent = `Shipping calculated at checkout · free on orders ${moneyShort(threshold)}+`;
      }
      if (ui.giftShip) ui.giftShip.hidden = status !== 'free';
    }
    async function refreshCart() {
      try {
        const cart = await fetch(`${cfg.root}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) => r.json());
        // Gift cards and digital items do not count toward the free-shipping subtotal.
        state.cartSubtotal = cart.items.filter((item) => item.requires_shipping && !item.gift_card).reduce((sum, item) => sum + item.final_line_price, 0);
        renderShipping(selection());
      } catch (e) {
        /* keep the last known subtotal */
      }
    }

    /* ---------- rendering ---------- */
    // The color's own product photo, cropped to the tee, over a tee drawn in the swatch color as fallback.
    const photo = (color, extra) => {
      const info = colorByName.get(color);
      const wrap = el('span', { class: `pc-bb__ph${extra ? ` ${extra}` : ''}`, html: svgTee(info ? info.swatch : '#d6d2ca') });
      if (info && info.photo) {
        const img = el('img', { src: info.photo, alt: '', loading: 'lazy', decoding: 'async' });
        img.addEventListener('error', () => img.remove());
        wrap.appendChild(img);
      }
      return wrap;
    };
    const dot = (color) => {
      const info = colorByName.get(color);
      return el('span', { class: 'pc-bb__dot', style: `--pc-swatch: ${info ? info.swatch : '#d6d2ca'}`, 'aria-hidden': 'true' });
    };
    // One thumbnail per color with its count, side by side, so black and navy never overlap.
    const thumbs = (parts) =>
      el(
        'span',
        { class: 'pc-bb__thumbs', 'aria-hidden': 'true' },
        parts.map((part) =>
          el('span', { class: 'pc-bb__thumb' }, [photo(part.color), el('span', { class: 'pc-bb__times', text: `×${part.quantity}` })])
        )
      );
    const breakdown = (parts) => parts.map((part) => `${part.quantity} ${part.color}`).join(' · ');
    const checkMark = () => el('span', { class: 'pc-bb__check', html: `${CHECK}<span class="pc-sr">Selected</span>` });

    function withFocus(render) {
      const active = document.activeElement;
      const key = active && root.contains(active) && !ui.dialog.contains(active) ? active.dataset.bbKey : null;
      render();
      if (key) {
        const again = root.querySelector(`[data-bb-key="${key}"]`);
        if (again && !again.disabled) again.focus({ preventScroll: true });
      }
    }

    const tierPrice = (qty) => {
      const list = tierPacks(qty);
      if (qty === 1) return teePrice(state.single || data.colors[0].name, state.size);
      return list.length ? Math.min(...list.map((p) => (packVariant(p, state.size) || p.variants[0]).price)) : customPrice(qty);
    };
    // The quantity with the lowest price per tee, from the prices on the page.
    const bestValueTier = () => {
      const priced = tiers.filter((qty) => qty > 1 && tierPrice(qty)).map((qty) => ({ qty, per: tierPrice(qty) / qty }));
      if (priced.length < 2) return null;
      priced.sort((a, b) => a.per - b.per);
      return priced[0].per < priced[1].per ? priced[0].qty : null;
    };

    // Badges sit above the quantity buttons: the lowest price per tee (worked out from the prices on the page) and the
    // best-seller quantity named in the theme editor. They never depend on which quantity is selected.
    const tierBadges = () => {
      const badges = {};
      const seller = Number(cfg.sellerQty);
      if (cfg.sellerLabel && tiers.includes(seller)) badges[seller] = { text: cfg.sellerLabel, note: cfg.sellerLabel };
      // "Lowest price per tee" is reserved for the ten-pack, and only while the live prices make it true.
      const best = cfg.valueLabel ? bestValueTier() : null;
      if (best === 10) badges[best] = { text: cfg.valueLabel, note: cfg.valueLabel };
      return badges;
    };

    // Horizontal rails (quantity cards, color cards). Scrolling is always done on the rail itself, never with
    // scrollIntoView, so the page cannot be dragged sideways or jump vertically.
    const reveal = (rail, target, instant) => {
      if (!rail || !target) return;
      const pad = 12;
      const left = target.offsetLeft - rail.offsetLeft;
      const right = left + target.offsetWidth;
      let next = rail.scrollLeft;
      if (left - pad < rail.scrollLeft) next = Math.max(0, left - pad);
      else if (right + pad > rail.scrollLeft + rail.clientWidth) next = right + pad - rail.clientWidth;
      if (next !== rail.scrollLeft) rail.scrollTo({ left: next, behavior: instant ? 'auto' : calm() });
    };
    let firstPaint = true;

    function renderTiers() {
      const tierScroll = ui.tiers.scrollLeft;
      ui.tiers.textContent = '';
      ui.tiers.style.setProperty('--bb-tier-cols', String(tiers.length));
      const badges = tierBadges();
      tiers.forEach((qty) => {
        const price = tierPrice(qty);
        const sub = qty === 1 ? money(price) : `${money(Math.round(price / qty))}/tee`;
        const badge = badges[qty];
        const button = el(
          'button',
          {
            type: 'button',
            class: `pc-bb__tier${qty === state.qty ? ' is-selected' : ''}${badge ? ' has-badge' : ''}`,
            'aria-pressed': String(qty === state.qty),
            'aria-label': `${tierName(qty)}, ${qty === 1 ? money(price) : `${money(price)} total, ${money(Math.round(price / qty))} per tee`}${badge ? `, ${badge.note}` : ''}`,
            'data-bb-key': `tier-${qty}`,
            'data-bb-tier': qty,
          },
          [
            badge ? el('span', { class: 'pc-bb__tierbadge', text: badge.text }) : null,
            el('span', { class: 'pc-bb__tiername', text: tierName(qty) }),
            el('span', { class: 'pc-bb__tiersub', text: sub }),
          ]
        );
        button.addEventListener('click', () => setQty(qty));
        ui.tiers.appendChild(button);
      });
      ui.qtyValue.textContent = tierName(state.qty);
      ui.tiers.scrollLeft = tierScroll;
      reveal(ui.tiers, ui.tiers.querySelector('.is-selected'), firstPaint);
    }

    function renderCards(sel) {
      const oldRail = ui.cards.querySelector('[data-bb-rail]');
      const railScroll = oldRail && oldRail.dataset.bbRailQty === String(state.qty) ? oldRail.scrollLeft : 0;
      ui.cards.textContent = '';
      // The cards sit in one swipeable row; the optional customization disclosure stays full width underneath it.
      const rail = el('div', { class: 'pc-bb__rail', 'data-bb-rail': '', 'data-bb-rail-qty': String(state.qty) });
      ui.cards.appendChild(rail);
      const finishRail = () => {
        rail.scrollLeft = railScroll;
        reveal(rail, rail.querySelector('.is-selected'), firstPaint || !oldRail || oldRail.dataset.bbRailQty !== String(state.qty));
      };
      if (state.qty === 1) {
        ui.cards.className = 'pc-bb__cards pc-bb__cards--single';
        data.colors.forEach((color) => {
          const ok = colorOk(color.name);
          const selected = state.single === color.name;
          const card = el(
            'button',
            { type: 'button', class: `pc-bb__card pc-bb__card--color${selected ? ' is-selected' : ''}${ok ? '' : ' is-unavailable'}`, 'aria-pressed': String(selected), disabled: !ok, 'data-bb-key': `color-${color.handle}` },
            [
              photo(color.name),
              selected ? checkMark() : null,
              el('span', { class: 'pc-bb__cardname' }, [dot(color.name), el('span', { text: color.name })]),
              ok ? null : el('span', { class: 'pc-bb__cardsub', text: `Sold out in ${state.size}` }),
            ]
          );
          card.addEventListener('click', () => {
            state.single = color.name;
            update();
          });
          rail.appendChild(card);
        });
        finishRail();
        return;
      }
      const canCustom = !!customBar(state.qty);
      const list = tierPacks(state.qty);
      const showCustom = canCustom || cfg.showUnpricedCustom;
      const total = list.length;
      ui.cards.className = `pc-bb__cards pc-bb__cards--n${Math.min(total, 4)}`;

      // Banner bars across the top of the cards. When any card in the row has one, every card keeps the same strip
      // so thumbnails and color lines stay aligned.
      const barred = list.some((pack) => pack.badge);
      // Build Your Own sits second in the row, straight after the best-selling preset, wherever a custom pack can
      // actually be bought. Where it cannot (no custom price for this pack size), the disabled card stays last.
      let customAdded = false;
      const addCustom = () => {
        if (customAdded || !showCustom) return;
        customAdded = true;
        const selected = sel.kind === 'custom';
        const chosen = countColors(visibleSlots());
        const missing = state.qty - filled();
        const sub = !canCustom
          ? customTiers.length
            ? `Available in ${customTiers.map(tierName).join(' and ')}`
            : 'Not available yet'
          : selected && chosen.length
            ? `${breakdown(chosen)}${missing > 0 ? ` · ${missing} to choose` : ''}`
            : 'Choose every color yourself';
        const custom = el(
          'button',
          { type: 'button', class: 'pc-bb__cardmain', 'aria-pressed': String(selected), disabled: !canCustom, 'data-bb-key': 'custom', 'aria-label': `${cfg.customLabel}: ${sub}` },
          [
            el('span', { class: 'pc-bb__thumbs pc-bb__thumbs--empty', 'aria-hidden': 'true', html: '<span class="pc-bb__plus">+</span>' }),
            el('span', { class: 'pc-bb__mix', text: cfg.customLabel }),
          ]
        );
        custom.addEventListener('click', buildYourOwn);
        const customCard = el('div', { class: `pc-bb__card pc-bb__card--pack pc-bb__card--custom${barred ? ' pc-bb__card--barred' : ''}${selected ? ' is-selected' : ''}${canCustom ? '' : ' is-unavailable'}` }, [
          barred ? el('span', { class: 'pc-bb__bar is-empty', 'aria-hidden': 'true' }) : null,
          selected ? checkMark() : null,
          custom,
        ]);
        if (canCustom) {
          const pick = el('button', { type: 'button', class: 'pc-bb__edit', 'data-bb-key': 'custom-edit', 'aria-label': `${cfg.customLabel}: ${sub}`, text: selected && chosen.length ? 'Edit colors' : 'Choose colors' });
          pick.addEventListener('click', buildYourOwn);
          customCard.appendChild(pick);
        }
        rail.appendChild(customCard);
      };
      // The best-selling (default) preset leads the row.
      const leadPack = defaultPreset(state.qty);
      const ordered = leadPack ? [leadPack, ...list.filter((pack) => pack !== leadPack)] : list;
      ordered.forEach((pack, index) => {
        const variant = state.size ? packVariant(pack, state.size) : pack.variants[0];
        const ok = !!variant && variant.available;
        const selected = sel.kind === 'preset' && sel.pack.handle === pack.handle;
        const badge = pack.badge || null;
        // The pack's name stays in the accessible label; on screen the card is its thumbnails and one color line.
        const select = el(
          'button',
          { type: 'button', class: 'pc-bb__cardmain', 'aria-pressed': String(selected), disabled: !ok, 'data-bb-key': `pack-${pack.handle}`, 'aria-label': `${shortName(pack)}: ${pack.summary}${badge ? `, ${badge}` : ''}${ok ? '' : `, sold out in ${state.size}`}` },
          // Thumbnails (one per color, with its count) and the pack's short name. The full color list is the
          // "Colors:" line above the row, so the card itself stays small.
          [thumbs(pack.contents), el('span', { class: 'pc-bb__mix', text: ok ? shortName(pack) : `Sold out in ${state.size}` })]
        );
        select.addEventListener('click', () => {
          state.choice[state.qty] = { mode: 'preset', handle: pack.handle };
          state.notice = '';
          update();
        });
        const card = el('div', { class: `pc-bb__card pc-bb__card--pack${barred ? ' pc-bb__card--barred' : ''}${selected ? ' is-selected' : ''}${ok ? '' : ' is-unavailable'}` }, [
          barred ? el('span', { class: `pc-bb__bar${badge ? '' : ' is-empty'}`, text: badge || '', 'aria-hidden': 'true' }) : null,
          selected ? checkMark() : null,
          select,
        ]);
        if (canCustom && ok) {
          const edit = el('button', { type: 'button', class: 'pc-bb__edit', 'data-bb-key': `edit-${pack.handle}`, 'aria-label': `Customize ${shortName(pack)}: keep its tees and change individual colors`, text: 'Customize' });
          edit.addEventListener('click', () => customizeFrom(pack, 0, `edit-${pack.handle}`));
          card.appendChild(edit);
        }
        rail.appendChild(card);
        if (index === 0 && canCustom) addCustom();
      });
      addCustom();

      finishRail();
    }

    const slotContent = (color, index) => [
      color ? photo(color) : el('span', { class: 'pc-bb__ph pc-bb__ph--empty', html: svgTee('none') }),
      el('span', { class: 'pc-bb__slotname', text: color || 'Add tee' }),
      el('span', { class: 'pc-bb__slotsize', text: color && !colorOk(color) ? 'Sold out' : state.size || `Tee ${index + 1}` }),
    ];
    const slotButton = (color, index, scope) =>
      el(
        'button',
        {
          type: 'button',
          class: `pc-bb__slot${color ? '' : ' is-empty'}${color && !colorOk(color) ? ' is-unavailable' : ''}`,
          'aria-label': color ? `Tee ${index + 1}: ${color}${state.size ? `, size ${state.size}` : ''}. Change color` : `Tee ${index + 1}: no color yet. Choose a color`,
          'data-bb-key': `${scope}-slot-${index}`,
        },
        slotContent(color, index)
      );

    function renderSummary(sel, ready) {
      const qty = state.qty;
      const price = sel.price;
      ui.colorValue.textContent = sel.label || (qty === 1 ? 'Choose a color' : sel.kind === 'custom' ? 'Choose your colors' : 'Choose a combination');
      const saving = price !== null && price !== undefined && sel.compare > price ? sel.compare - price : 0;
      // Left: price per tee. Right: the amount actually charged for the selection, with the singles comparison.
      const perNode = root.querySelector('[data-bb-per]');
      const totalWrap = root.querySelector('[data-bb-total-wrap]');
      if (perNode) perNode.textContent = price === null || price === undefined ? '' : money(Math.round(price / qty));
      if (totalWrap) totalWrap.hidden = qty === 1;
      if (price === null || price === undefined) {
        ui.now.textContent = '';
        ui.was.hidden = true;
      } else {
        ui.now.textContent = money(price);
        ui.was.hidden = !saving;
        ui.was.innerHTML = saving ? `<span class="pc-sr">Regular price </span>${money(sel.compare)}` : '';
      }
      // Pack summary beside the purchase controls.
      ui.sumPack.textContent = `${qty === 1 ? 'Single tee' : tierName(qty)} · ${state.size ? `Size ${state.size}` : 'Size not chosen'}`;
      ui.sumColors.textContent = sel.label || (qty === 1 ? 'Color not chosen' : 'Colors not chosen');
      if (ui.gifts) ui.gifts.hidden = !(qty > 1 && ((sel.kind === 'preset' && sel.pack.gifts) || (sel.kind === 'custom' && customGifts(customBar(qty)).length) || (sel.kind === 'none' && cfg.giftsOk)));
      if (ui.giftLine) {
        // Savings are on the tees; the gift card is an extra, never part of the amount saved or payable.
        ui.giftLine.textContent = saving ? `This pack saves ${money(saving)} on tees versus singles; the gifts are extra.` : '';
      }
      renderShipping(sel);

      const label = state.busy ? 'Adding…' : ready.ready ? `${cfg.atcLabel} — ${money(price)}` : ready.label;
      ui.atcLabel.textContent = label;
      ui.atc.classList.toggle('is-waiting', !ready.ready);
      ui.atc.classList.toggle('is-busy', state.busy);
      ui.atc.setAttribute('aria-disabled', String(!ready.ready || state.busy));
      if (stickyBtn) {
        // One sticky bar: the live price on the left, the next step (or Add to cart) on the right.
        stickyBtn.textContent = state.busy ? 'Adding…' : ready.ready ? cfg.atcLabel : ready.label;
        stickyBtn.setAttribute('aria-disabled', String(state.busy));
        if (stickyPrice) stickyPrice.textContent = price !== null && price !== undefined ? money(price) : '';
        if (stickySum) {
          stickySum.textContent = [qty === 1 ? 'Single tee' : tierName(qty), state.size ? `Size ${state.size}` : null, sel.label || null].filter(Boolean).join(' · ');
        }
      }
      ui.notice.textContent = state.notice;
      ui.notice.hidden = !state.notice;
    }

    function update() {
      const sel = selection();
      const ready = readiness(sel);
      ui.sizeValue.textContent = state.size || cfg.sizePrompt;
      root.querySelectorAll('[data-pc-sg-open]').forEach((opener) => {
        opener.dataset.pcSgSize = state.size || '';
      });
      withFocus(() => {
        renderTiers();
        renderCards(sel);
      });
      renderSummary(sel, ready);
      // A message about a missing step belongs to the click that raised it; any later change clears it.
      ui.sizeError.hidden = true;
      ui.sizeGroup.classList.remove('has-error');
      ui.colorError.hidden = true;
      if (ui.dialog.open) renderDialog();
      remember();
      firstPaint = false;
    }

    /* ---------- quantity changes ---------- */
    function setQty(qty) {
      // Tapping the quantity that is already selected keeps every edit.
      if (qty === state.qty) return;
      state.qty = qty;
      state.notice = '';
      // A new quantity always starts from its featured preset (the one with the banner). A custom build belongs to
      // the quantity it was made for, so it is cleared here; the size stays as chosen.
      state.dirty = false;
      for (let i = 0; i < state.slots.length; i += 1) state.slots[i] = null;
      Object.keys(state.choice).forEach((key) => delete state.choice[key]);
      if (qty > 1) {
        const preset = defaultPreset(qty);
        if (preset) state.choice[qty] = { mode: 'preset', handle: preset.handle };
      }
      update();
      const sel = selection();
      ui.status.textContent = `${tierName(qty)} selected${sel.price ? `, ${money(sel.price)} total` : ''}${sel.label ? `, ${sel.label}` : ''}.`;
    }

    /* ---------- pack editor ---------- */
    let dialogOpener = null;
    // Edit a preset: its exact tees become the editable pack, nothing is cleared.
    function customizeFrom(pack, index, openerKey) {
      const current = isCustom() && filled() === state.qty && sameColors(visibleSlots(), expand(pack));
      if (!current) {
        expand(pack).forEach((color, i) => {
          state.slots[i] = color;
        });
        state.dirty = false;
      }
      state.choice[state.qty] = { mode: 'custom' };
      state.notice = '';
      update();
      openDialog(index, openerKey);
    }
    // Build Your Own: back to the customer's own build if there is one, otherwise a clean start.
    function buildYourOwn() {
      if (!customBar(state.qty)) return;
      if (!isCustom() || !state.dirty) {
        if (!state.dirty) {
          for (let i = 0; i < state.slots.length; i += 1) state.slots[i] = null;
          state.dirty = true;
        }
        state.choice[state.qty] = { mode: 'custom' };
        state.notice = '';
        update();
      }
      openDialog(Math.max(0, visibleSlots().indexOf(null)), 'custom');
    }
    function openDialog(index, openerKey) {
      state.customOpen = true;
      state.active = Math.min(Math.max(0, index), state.qty - 1);
      dialogOpener = openerKey || null;
      renderDialog();
      if (!ui.dialog.open) {
        if (typeof ui.dialog.showModal === 'function') ui.dialog.showModal();
        else ui.dialog.setAttribute('open', '');
        document.documentElement.classList.add('pc-scroll-lock');
      }
      const target = ui.dColors.querySelector('button[aria-pressed="true"]:not([disabled])') || ui.dColors.querySelector('button:not([disabled])');
      if (target) target.focus({ preventScroll: true });
    }
    function closeDialog() {
      if (ui.dialog.open) {
        if (typeof ui.dialog.close === 'function') ui.dialog.close();
        else ui.dialog.removeAttribute('open');
      }
    }
    ui.dialog.addEventListener('close', () => {
      if (!document.querySelector('dialog[open]')) document.documentElement.classList.remove('pc-scroll-lock');
      update();
      const opener = dialogOpener && root.querySelector(`[data-bb-key="${dialogOpener}"]`);
      const custom = root.querySelector('[data-bb-key="custom"]:not([disabled])');
      const fallback = custom && custom.offsetParent ? custom : ui.atc;
      (opener && !opener.disabled && opener.offsetParent ? opener : fallback).focus({ preventScroll: true });
      const missing = state.qty - filled();
      ui.status.textContent = missing > 0 ? `${filled()} of ${state.qty} tees selected.` : `Pack complete: ${summarize(visibleSlots())}.`;
    });
    ui.dialog.addEventListener('click', (event) => {
      if (event.target === ui.dialog || event.target.closest('[data-bb-d-close]')) closeDialog();
    });
    ui.dDone.addEventListener('click', closeDialog);
    ui.dClear.addEventListener('click', () => {
      state.slots[state.active] = null;
      state.dirty = true;
      update();
    });
    ui.dReset.addEventListener('click', () => {
      for (let i = 0; i < state.qty; i += 1) state.slots[i] = null;
      state.dirty = true;
      state.active = 0;
      update();
      const target = ui.dColors.querySelector('button:not([disabled])');
      if (target) target.focus({ preventScroll: true });
    });

    function renderDialog() {
      const qty = state.qty;
      const count = filled();
      const price = selection().price;
      ui.dName.textContent = `Customize your ${tierName(qty)}`;
      ui.dSub.textContent = `${state.size ? `Size ${state.size} for every tee` : 'Choose your size in the buy box'}${price ? ` · ${money(price)} total` : ''}`;
      ui.dCount.textContent = `${count} of ${qty} tees selected`;
      ui.dBar.style.width = `${Math.round((count / qty) * 100)}%`;
      ui.dDone.textContent = count === qty ? 'Confirm pack' : `Done · ${count} of ${qty}`;
      ui.dClear.disabled = !state.slots[state.active];
      ui.dReset.disabled = count === 0;

      const focusKey = document.activeElement && ui.dialog.contains(document.activeElement) ? document.activeElement.dataset.bbKey : null;
      ui.dSlots.textContent = '';
      ui.dSlots.style.setProperty('--bb-cols', String(Math.min(qty, 5)));
      visibleSlots().forEach((color, index) => {
        const button = slotButton(color, index, 'd');
        if (index === state.active) {
          button.classList.add('is-active');
          button.setAttribute('aria-current', 'true');
        }
        button.addEventListener('click', () => {
          state.active = index;
          renderDialog();
        });
        ui.dSlots.appendChild(el('li', {}, [button]));
      });
      const current = state.slots[state.active];
      ui.dPick.classList.toggle('is-first', count === 0);
      ui.dPick.textContent = count === 0 ? 'Choose your first color' : current ? `Tee ${state.active + 1} of ${qty}: ${current} — choose a color to replace it` : `Tee ${state.active + 1} of ${qty}: choose a color`;
      ui.dColors.textContent = '';
      data.colors.forEach((color) => {
        const ok = colorOk(color.name);
        const pressed = current === color.name;
        const button = el('button', { type: 'button', class: `pc-bbd__color${pressed ? ' is-selected' : ''}`, 'aria-pressed': String(pressed), disabled: !ok, 'data-bb-key': `d-color-${color.handle}` }, [
          el('span', { class: 'pc-bbd__swatch', style: `--pc-swatch: ${color.swatch}` }),
          el('span', { class: 'pc-bbd__colorname', text: ok ? color.name : `${color.name} (sold out in ${state.size})` }),
          pressed ? el('span', { class: 'pc-bbd__tick', html: CHECK }) : null,
        ]);
        button.addEventListener('click', () => {
          const previous = state.slots[state.active];
          if (previous === color.name) return;
          state.slots[state.active] = color.name;
          state.dirty = true;
          // Filling an empty slot moves on to the next empty one; replacing a color stays on that tee.
          if (!previous) {
            const next = visibleSlots().indexOf(null);
            if (next > -1) state.active = next;
          }
          update();
        });
        ui.dColors.appendChild(button);
      });
      if (focusKey) {
        const again = ui.dialog.querySelector(`[data-bb-key="${focusKey}"]`);
        if (again && !again.disabled) again.focus({ preventScroll: true });
        else ui.dDone.focus({ preventScroll: true });
      }
    }

    /* ---------- cart ---------- */
    const dealCache = new Map();
    const loadDeal = (url) => {
      if (!dealCache.has(url)) {
        const request = fetch(url, { credentials: 'same-origin' })
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.text();
          })
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            return {
              products: [...doc.querySelectorAll('script.kaching-bundles-product')].map(readJson).filter(Boolean),
              deals: [...doc.querySelectorAll('script.kaching-bundles-deal-block-settings')].map(readJson).filter(Boolean),
            };
          });
        request.catch(() => dealCache.delete(url));
        dealCache.set(url, request);
      }
      return dealCache.get(url);
    };
    const hasPromoEbook = (cart, productId) => cart.items.some((item) => item.product_id === productId && kachingData(item) && kachingData(item).gift);
    // Real product gifts of a deal bar ("free shipping" style gifts have no product).
    function customGifts(bar) {
      if (!bar) return [];
      return (bar.freeGifts || [])
        .filter((gift) => gift.giftType === 'product' && gift.productGID)
        .map((gift) => {
          const product = kachingProducts.find((p) => p.id === numericId(gift.productGID));
          if (!product) return null;
          const variant =
            gift.variantGIDs && gift.variantGIDs.length
              ? (product.variants || []).find((v) => v.id === numericId(gift.variantGIDs[0]))
              : (product.variants || []).find((v) => v.availableForSale);
          return variant && variant.availableForSale ? { gift, product, variant } : null;
        })
        .filter(Boolean);
    }

    // Preset pack: the same request the collection quick-add sends (bundle "main" line + its Kaching gift lines).
    async function presetItems(sel, cart) {
      const main = { id: sel.variant.id, quantity: 1, properties: { Colors: sel.pack.summary || summarize(sel.colors) } };
      if (!sel.pack.gifts) return [main];
      const info = await loadDeal(sel.pack.url);
      const product = info.products.find((p) => (p.variants || []).some((v) => v.id === sel.variant.id));
      const withGifts = info.deals.filter((d) => (d.dealBars || []).some((b) => b.freeGifts && b.freeGifts.length));
      const packDeal = product && (withGifts.find((d) => Array.isArray(d.selectedProductIds) && d.selectedProductIds.includes(product.id)) || (withGifts.length === 1 ? withGifts[0] : null));
      const bars = (packDeal && packDeal.dealBars) || [];
      const bar = bars.find((b) => b.id === packDeal.preselectedDealBarId) || bars.find((b) => b.freeGifts && b.freeGifts.length);
      if (!packDeal || !packDeal.nanoId || !bar || !bar.freeGifts || !bar.freeGifts.length || bar.quantity !== 1) {
        throw new Error("We couldn't add the free gifts right now. Please try again.");
      }
      const group = groupId();
      const common = packDeal.versionId ? { deal: packDeal.nanoId, v: packDeal.versionId } : { deal: packDeal.nanoId };
      const items = [];
      for (const gift of bar.freeGifts) {
        const productId = numericId(gift.productGID);
        const giftProduct = info.products.find((p) => p.id === productId);
        if (!giftProduct) throw new Error("We couldn't add the free gifts right now. Please try again.");
        const variant =
          gift.variantGIDs && gift.variantGIDs.length
            ? (giftProduct.variants || []).find((v) => v.id === numericId(gift.variantGIDs[0]))
            : (giftProduct.variants || []).find((v) => v.availableForSale);
        if (!variant || !variant.availableForSale) throw new Error("We couldn't add the free gifts right now. Please try again.");
        // The gift card comes with every bundle; any other gift (the e-book) is added once per cart.
        if (giftProduct.handle !== cfg.giftCardHandle && hasPromoEbook(cart, productId)) continue;
        items.push({ id: variant.id, quantity: gift.quantity || 1, properties: { __kaching_bundles: JSON.stringify({ id: group, ...common, gift: gift.id }) } });
      }
      main.properties.__kaching_bundles = JSON.stringify({ ...common, pp: product.id, main: true, id: group, bid: bar.id });
      items.push(main);
      return items;
    }

    // Custom pack: Kaching's own quantity-break request for this tee count (one "main" line per variant, gifts nested
    // under the first), so Kaching's discount sets the pack price at checkout.
    function customItems(sel, cart) {
      const bar = customBar(state.qty);
      if (!bar || !deal) throw new Error(`Custom ${tierName(state.qty)}s can't be added yet. Choose a ready-made combination.`);
      const counts = new Map();
      sel.colors.forEach((color) => {
        const variant = teeVariant(color, state.size);
        if (!variant || !variant.available) throw new Error(`${color} is sold out in ${state.size}.`);
        counts.set(variant.id, (counts.get(variant.id) || 0) + 1);
      });
      const total = [...counts.values()].reduce((sum, n) => sum + n, 0);
      if (total !== state.qty) throw new Error(`Choose exactly ${state.qty} tees.`);
      const group = groupId();
      const common = deal.versionId ? { deal: deal.nanoId, v: deal.versionId } : { deal: deal.nanoId };
      const mainProps = () => ({
        Pack: `Custom ${tierName(state.qty)} · size ${state.size} · ${summarize(sel.colors)}`,
        __kaching_bundles: JSON.stringify({ deal: common.deal, pp: data.productId, main: true, ...(common.v ? { v: common.v } : {}), id: group, bid: bar.id }),
      });
      const mains = [...counts.entries()].map(([id, quantity]) => ({ id, quantity, properties: mainProps() }));
      const gifts = customGifts(bar)
        .filter(({ product }) => product.handle === cfg.giftCardHandle || !hasPromoEbook(cart, product.id))
        .map(({ gift, variant }) => ({
          id: variant.id,
          quantity: gift.quantity || 1,
          parent_id: mains[0].id,
          properties: { __kaching_bundles: JSON.stringify({ id: group, ...common, gift: gift.id }) },
        }));
      return [mains[0], ...gifts, ...mains.slice(1)];
    }

    /* Last check on the request itself (layout/theme.liquid runs these guards on the final /cart/add request, after any
       app has had its turn). A buy-box request must reach the cart exactly as built, and nothing else on this page may
       add this tee as a Kaching bundle: either case is refused before it reaches the cart. */
    let expected = null;
    const teeIds = new Set(data.variants.map((v) => String(v.id)));
    const requestItems = (body) => {
      const items = {};
      const take = (key, value) => {
        const field = /^items\[(\d+)\]\[(id|quantity)\]$/.exec(key);
        if (field) (items[field[1]] = items[field[1]] || {})[field[2]] = String(value);
        const prop = /^items\[(\d+)\]\[properties\]\[__kaching_bundles\]$/.exec(key);
        if (prop) (items[prop[1]] = items[prop[1]] || {}).kaching = String(value);
        if (key === 'id') (items.form = items.form || {}).id = String(value);
        if (key === 'quantity') (items.form = items.form || {}).quantity = String(value);
      };
      if (body instanceof FormData || body instanceof URLSearchParams) {
        for (const [key, value] of body.entries()) take(key, value);
      } else if (typeof body === 'string') {
        try {
          const parsed = JSON.parse(body);
          (parsed.items || [parsed]).forEach((item, i) => {
            items[i] = { id: String(item.id), quantity: String(item.quantity || 1), kaching: item.properties && item.properties.__kaching_bundles };
          });
        } catch (e) {
          new URLSearchParams(body).forEach((value, key) => take(key, value));
        }
      }
      return Object.values(items).filter((item) => item.id);
    };
    const refuse = (text) =>
      new Response(JSON.stringify({ status: 422, message: 'Cart request refused', description: text }), { status: 422, headers: { 'Content-Type': 'application/json' } });
    const guard = (url, init) => {
      if (!/\/cart\/add(\.js)?(\?|$)/.test(url) || !init || !init.body || !document.contains(root)) return null;
      const sent = requestItems(init.body);
      if (expected) {
        const want = expected;
        expected = null;
        const key = (list) => list.map((item) => `${item.id}x${item.quantity || 1}`).sort().join(',');
        return key(sent) === key(want) ? null : refuse("We couldn't add your selection exactly as chosen. Please reload the page and try again.");
      }
      const foreign = sent.some((item) => teeIds.has(item.id) && item.kaching && /"main":true/.test(item.kaching));
      return foreign ? refuse('Choose your size and colors in the buy box, then add to cart.') : null;
    };
    if (Array.isArray(window.PrimeCutCartAddGuards)) window.PrimeCutCartAddGuards.push(guard);

    const showError = (node, text) => {
      node.textContent = text;
      node.hidden = false;
    };

    async function add() {
      if (state.busy) return;
      const sel = selection();
      const ready = readiness(sel);
      ui.addError.hidden = true;
      if (!ready.ready) {
        // Point at the missing step instead of adding anything.
        if (ready.step === 'size') {
          showError(ui.sizeError, ready.message);
          ui.sizeGroup.classList.add('has-error');
          const first = root.querySelector('[data-bb-size]:not([disabled])');
          ui.sizeGroup.scrollIntoView({ block: 'center', behavior: calm() });
          if (first) first.focus({ preventScroll: true });
        } else if (ready.step === 'pack') {
          // An unfinished custom pack is finished in the editor: open it on the first tee that still needs a color.
          const slots = visibleSlots();
          const gap = slots.findIndex((color) => !color || !colorOk(color));
          openDialog(Math.max(0, gap), 'custom');
        } else {
          showError(ui.colorError, ready.message);
          const target = ui.cards.querySelector('button:not([disabled])');
          if (target) {
            target.scrollIntoView({ block: 'center', behavior: calm() });
            target.focus({ preventScroll: true });
          }
        }
        return;
      }
      state.busy = true;
      update();
      const drawer = document.querySelector('cart-drawer');
      const sections = drawer && typeof drawer.getSectionsToRender === 'function' ? drawer.getSectionsToRender().map((s) => s.id) : null;
      try {
        let items;
        if (sel.kind === 'single') {
          items = [{ id: sel.variant.id, quantity: 1 }];
        } else {
          const cart = await fetch(`${cfg.root}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) => r.json());
          items = sel.kind === 'preset' ? await presetItems(sel, cart) : customItems(sel, cart);
        }
        const body = new FormData();
        items.forEach((item, i) => {
          body.append(`items[${i}][id]`, String(item.id));
          body.append(`items[${i}][quantity]`, String(item.quantity));
          if (item.parent_id) body.append(`items[${i}][parent_id]`, String(item.parent_id));
          Object.entries(item.properties || {}).forEach(([key, value]) => body.append(`items[${i}][properties][${key}]`, value));
        });
        if (sections) {
          body.append('sections', sections.join(','));
          body.append('sections_url', window.location.pathname);
        }
        expected = items.map((item) => ({ id: String(item.id), quantity: String(item.quantity) }));
        const response = await fetch(`${cfg.root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' }, body });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || "We couldn't add this to your cart. Please try again.");
        ui.status.textContent = `${tierName(state.qty)} added to cart.`;
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'pc-buybox', productVariantId: items[items.length - 1].id, cartData: result });
        }
        if (drawer && typeof drawer.renderContents === 'function' && result.sections) {
          drawer.classList.remove('is-empty');
          drawer.renderContents(result);
        } else {
          window.location.assign(`${cfg.root}cart`);
        }
        refreshCart();
      } catch (err) {
        const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
        ui.atc.scrollIntoView({ block: 'center', behavior: calm() });
        showError(ui.addError, offline ? "We couldn't reach the cart. Check your connection and try again." : err && err.message && err.message !== 'Failed to fetch' ? err.message : "We couldn't add this to your cart. Please try again.");
      } finally {
        expected = null;
        state.busy = false;
        update();
      }
    }

    /* ---------- events ---------- */
    root.querySelectorAll('[data-bb-size]').forEach((input) =>
      input.addEventListener('change', () => {
        if (!input.checked) return;
        state.size = input.value;
        update();
        ui.status.textContent = `Size ${state.size} selected for every tee.`;
      })
    );
    ui.atc.addEventListener('click', add);
    if (stickyBtn) {
      // Incomplete: add() moves to the next required step (size, colors or the pack editor) and says what is missing.
      // Complete: it adds exactly what is selected, through the same handler as the main button (state.busy stops
      // a second add while one is in flight).
      stickyBtn.addEventListener('click', (event) => {
        event.preventDefault();
        add();
      });
    }
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (!event || event.source !== 'pc-buybox') refreshCart();
      });
    }
    document.addEventListener('cart:updated', refreshCart);
    // Returning to the page (back button, another tab) may bring a different cart.
    window.addEventListener('pageshow', refreshCart);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) refreshCart();
    });
    // The drawer re-renders whenever the cart changes (quantity steppers, removals, other add buttons).
    const drawerEl = document.querySelector('cart-drawer');
    if (drawerEl && 'MutationObserver' in window) {
      let drawerTimer = 0;
      new MutationObserver(() => {
        clearTimeout(drawerTimer);
        drawerTimer = setTimeout(refreshCart, 600);
      }).observe(drawerEl, { childList: true, subtree: true });
    }

    renderArrival();
    update();
    // Anything the shopper does inside the buy box from here on counts as their own choice.
    root.addEventListener('click', () => {
      touched = true;
    }, true);
    root.addEventListener('change', () => {
      touched = true;
    }, true);
    refreshCart();
    // Read the selected preset's gift deal ahead of the click, so adding is one request.
    const lead = selectedPack();
    if (lead && lead.gifts) loadDeal(lead.url).catch(() => {});
  }

  const boot = (scope) => scope.querySelectorAll('[data-pc-bb]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(document));
  else boot(document);
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
